import classNames from "classnames";
import { FieldAttributes } from "formik";
import React from "react";
import styled, { css } from "styled-components";
import { Button } from "./Button";
import { Icon } from "./Icon";
import {
  BaseInput,
  ErrorMessage,
  ErrorText,
  FocusOutline,
  InputProps,
  InputWrapper,
  Label,
  RequiredAsterisk,
  Wrapper,
} from "./TextInputUtils";
import { Tooltip } from "./Tooltip";
import { useFirstRequiredErrorInForm } from "./hooks/useFirstRequiredErrorInForm";
import { LoadingState } from "./states/LoadingState";
import { style } from "./style";
import { deprecationWarning } from "./utils/warning";

/**
 * @deprecated Use `TextInputV2` instead.
 */
export const Input = styled(BaseInput)<{
  $leftIcon?: boolean;
  $rightElem?: boolean;
  $readOnlyStyling?: boolean;
}>`
  && {
    position: relative;
    z-index: 1;
    display: block;
    border: ${style.vars.borderWidth.thin} solid ${style.colors.grey.dark};
    color: ${style.colors.grey.darkest};
    width: 100%;
    ${({ $readOnlyStyling, $leftIcon }) =>
      $readOnlyStyling &&
      css`
        &:disabled {
          opacity: 1;
          color: inherit;
          border-color: transparent;
          background-color: transparent;
          cursor: not-allowed;
          padding-left: ${$leftIcon ? "48px" : "0px"};
        }
      `}
    ${({ $leftIcon }) =>
      $leftIcon &&
      css`
        padding-left: 48px;
      `}
    ${({ $rightElem }) =>
      $rightElem &&
      css`
        padding-right: 48px;
      `}
  }
`;

const LeftIconWrapper = styled.div`
  position: absolute;
  z-index: ${style.vars.zIndex.base};
  left: ${style.vars.space["3"]};
  top: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

type RightItemsWrapperProps = {
  $noPadding: boolean;
};

const RightItemsWrapper = styled.div<RightItemsWrapperProps>`
  position: absolute;
  z-index: ${style.vars.zIndex.base};
  right: ${(props) => (props.$noPadding ? style.vars.space["1"] : style.vars.space["3"])};
  top: 0;
  bottom: 0;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
`;

const ErrorMessageIcon = styled(Icon)`
  margin-right: ${style.vars.whitespace.xsmall};
  flex-shrink: 0;
`;

/**
 * @deprecated Use `TextInputV2` instead.
 */
export const TextInput: React.FC<InputProps | FieldAttributes<any>> = ({
  type = "text",
  required = false,
  disabled = false,
  loading = false,
  mono = false,
  isSecret = false,
  readOnly = false,
  name,
  label,
  labelTooltip,
  description,
  value,
  placeholder,
  error,
  className,
  touched,
  rightIcon,
  leftIcon,
  ...inputProps
}) => {
  deprecationWarning("TextInput", "TextInputV2.X");

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const [secretVisible, setSecretVisibility] = React.useState(false);

  const ref = React.useRef<HTMLInputElement | null>(null);
  const { hasRequiredError, onInput } = useFirstRequiredErrorInForm(ref);

  const hasPropError = error && touched;
  const isInvalid = hasPropError || hasRequiredError;
  const rightElements = rightIcon || isSecret;

  // Disabled "overrides" readOnly styling
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const readOnlyStyling = readOnly && !disabled;

  const classes = classNames({
    [className]: className,
    "is-invalid": isInvalid,
    "is-required": required,
  });

  return (
    <Wrapper className={classes}>
      <Label data-testid={`${name}-label`} htmlFor={name} $disabled={disabled}>
        {label}
        {required && <RequiredAsterisk>*</RequiredAsterisk>}
        {labelTooltip && <Tooltip.Help content={labelTooltip} />}
      </Label>
      <div>{description}</div>
      <InputWrapper>
        <Input
          id={name}
          data-testid={`${name}-input`}
          type={isSecret ? (secretVisible ? "text" : "password") : type}
          value={value}
          required={required}
          placeholder={placeholder}
          disabled={disabled || readOnly}
          $leftIcon={leftIcon}
          $rightElem={rightElements}
          $readOnlyStyling={readOnlyStyling}
          $mono={mono}
          innerRef={ref}
          onInput={onInput}
          {...inputProps}
        />
        <FocusOutline />
        <RightItemsWrapper $noPadding={isSecret && !loading}>
          {loading ? (
            <LoadingState />
          ) : (
            <>
              {rightIcon && <Icon large icon={rightIcon} />}
              {isSecret && !readOnly && (
                <Button
                  icon={secretVisible ? "eye-show" : "eye-hide"}
                  variation="icon"
                  iconSize="16px"
                  disabled={disabled}
                  type="button"
                  onClick={() => setSecretVisibility(!secretVisible)}
                />
              )}
            </>
          )}
        </RightItemsWrapper>
        {leftIcon && (
          <LeftIconWrapper>
            <Icon large icon={leftIcon} />
          </LeftIconWrapper>
        )}
      </InputWrapper>
      {(hasRequiredError || (hasPropError && typeof error === "string")) && (
        <ErrorMessage data-testid={`${name}-error`}>
          {hasPropError && <ErrorMessageIcon large icon="error" />}
          <ErrorText className="required-err">{hasPropError ? error : label + " is required"}</ErrorText>
        </ErrorMessage>
      )}
    </Wrapper>
  );
};
